import { useRef, useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Snackbar
} from '@mui/material';
import { CloudUpload, Download, Delete } from '@mui/icons-material';

import { AdminRoles, DoctorRoles, Phase } from '../../config/DiactiveTypes';
import { formatDateTime, formatFileSize, minDaysForPostEval } from '../../utils/DiactiveUtils';
import { uploadGlucoseFile, fetchGlucoseFiles, getFileDownloadUrl, deleteGlucoseFile } from '../../services/StorageService';
import { getAuthUserId } from '../../services/AuthService';
import { resolveUserDisplayNames, displayNameForUser } from '../../utils/UserDisplayUtils';
import ConfirmDialog from '../common/ConfirmDialog';
import SendEmailButton from '../common/SendEmailButton';

const MAX_FILE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_MB * 1024 * 1024;

const GlucoseFiles = ({
  patientId,
  role,
  daysRemaining,
  isDoctorPreviewMode = false,
  onDoctorPreviewBlockedAction = () => {},
  onSendReminderClick
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState({ pre: false, post: false });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const preFileInputRef = useRef();
  const postFileInputRef = useRef();

  // Filter files by phase
  const preFiles = files.filter(file => file.phase === 'pre');
  const postFiles = files.filter(file => file.phase === 'post');

  // Fetch files on mount
  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      try {
        const fetchedFiles = await fetchGlucoseFiles(patientId);
        const namesById = await resolveUserDisplayNames(
          fetchedFiles.map((f) => f.uploaderId)
        );
        setFiles(
          fetchedFiles.map((f) => ({
            ...f,
            uploaderName: f.uploaderId ? (namesById[f.uploaderId] ?? null) : null,
          }))
        );
      } catch (err) {
        setUploadError("Error al cargar los archivos.");
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, [patientId]);

  const handleFileUpload = async (file, phase, errorMsg) => {
    if (errorMsg) {
      setUploadError(errorMsg);
      return;
    }
    setUploadError(null);
    setUploading(prev => ({ ...prev, [phase]: true }));
    try {
      const uploadedFile = await uploadGlucoseFile(patientId, file, phase);
      const uploaderId = uploadedFile.uploaderId ?? getAuthUserId();
      const namesById = await resolveUserDisplayNames([uploaderId]);
      setFiles((prev) => [
        {
          ...uploadedFile,
          uploaderName: displayNameForUser(namesById, uploaderId),
        },
        ...prev,
      ]);
    } catch (err) {
      setUploadError("Error al subir el archivo.");
    } finally {
      setUploading(prev => ({ ...prev, [phase]: false }));
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const url = await getFileDownloadUrl(file.storagePath);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setUploadError("No se pudo descargar el archivo.");
    }
  };

  const handleFileChange = (e, phase) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      handleFileUpload(null, phase, `El archivo es demasiado grande (máximo ${MAX_FILE_MB} MB)`);
      return;
    }
    handleFileUpload(file, phase);

    // Clear the input
    if (phase === 'pre') {
      preFileInputRef.current.value = "";
    } else {
      postFileInputRef.current.value = "";
    }
  };

  const handleFileDelete = async () => {
    try {
      await deleteGlucoseFile(patientId, fileToDelete);
      setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      setSnackbar({ open: true, message: 'El archivo se ha eliminado correctamente', severity: 'success' });
    } catch (err) {
      setUploadError("No se pudo borrar el archivo.");
    } finally {
      closeDeleteDialog();
    }
  };

  const openDeleteDialog = (file) => {
    setFileToDelete(file);
    setOpenConfirmDialog(true);
  };

  const closeDeleteDialog = () => {
    setFileToDelete(null);
    setOpenConfirmDialog(false);
  };

  // Check if post phase is available
  const isPostPhaseAvailable = daysRemaining <= minDaysForPostEval;
  const isDoctorRole = DoctorRoles.includes(role);
  const handleDoctorPreviewGuard = (callback) => {
    if (isDoctorPreviewMode && isDoctorRole) {
      onDoctorPreviewBlockedAction();
      return;
    }
    callback();
  };

  const renderFilesTable = (files, title, phase) => {
    const isDisabled = phase === 'post' && !isPostPhaseAvailable;

    return (
      <Box sx={{ mt: 6, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: isDisabled ? 'text.disabled' : 'text.primary' }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {AdminRoles.includes(role) && (
              <SendEmailButton
                text={`Recordar Archivo ${phase}`}
                onClick={() => onSendReminderClick(phase)}
                disabled={isDisabled}
              />
            )}
            {DoctorRoles.includes(role) && (
              <Box>
                <input
                  type="file"
                  style={{ display: 'none' }}
                  ref={phase === 'pre' ? preFileInputRef : postFileInputRef}
                  onChange={(e) => handleFileChange(e, phase)}
                />
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={() => handleDoctorPreviewGuard(() => {
                    if (phase === 'pre') {
                      preFileInputRef.current.click();
                    } else {
                      postFileInputRef.current.click();
                    }
                  })}
                  disabled={uploading[phase] || isDisabled}
                >
                  {uploading[phase] ? "Subiendo..." : `Subir archivo ${phase}`}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {isDisabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Los archivos post estarán disponibles cuando queden {minDaysForPostEval} días o menos 
            (actualmente quedan {daysRemaining} días)
          </Alert>
        )}

        <TableContainer sx={{ opacity: isDisabled ? 0.5 : 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Subido por</TableCell>
                <TableCell>Tamaño</TableCell>
                <TableCell>Descargar</TableCell>
                <TableCell>Borrar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file, idx) => (
                <TableRow key={file.id || idx}>
                  <TableCell>{formatDateTime(file.uploadDate)}</TableCell>
                  <TableCell>{file.name}</TableCell>
                  <TableCell>{file.uploaderName ?? file.uploaderId ?? '—'}</TableCell>
                  <TableCell>{formatFileSize(file.size)}</TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => handleFileDownload(file)}
                      disabled={isDisabled}
                    >
                      <Download color={isDisabled ? "disabled" : "primary"} />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => openDeleteDialog(file)} 
                      color="error"
                      disabled={isDisabled}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {files.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {isDisabled 
                        ? `Los archivos ${phase} estarán disponibles pronto`
                        : `No hay archivos subidos para esta fase`
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
      )}

      <Box sx={{ mt: 4 }}>
        {renderFilesTable(preFiles, "Archivos Pre", Phase.PRE)}
        {renderFilesTable(postFiles, "Archivos Post", Phase.POST)}
      </Box>

      <ConfirmDialog
        open={openConfirmDialog}
        title="Confirmar eliminación"
        description={"¿Seguro que quieres borrar este archivo? Esta acción no se puede deshacer."}
        onCancel={closeDeleteDialog}
        onConfirm={handleFileDelete}
        confirmButtonColor="error"
        confirmButtonText="Borrar"
      />
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ mt: 10 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlucoseFiles;